import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Toggle} from '../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {legacyInboxColorToPaletteColor} from '../../../libs/shared/ui/src/styles/colorHelpers';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {lightTheme} from '../../../libs/shared/ui/src/theme/light/lightTheme';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {PaletteColorPicker} from '../../components/common/color/paletteColorPicker';
import {isTeammateNamespace} from '../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {InboxModel} from '../../models/api/inboxModel';
import {SettingsInboxesUrl} from '../../models/url/settingsUrlModel';
import {getCompanyHasGlobalRules} from '../../store/data/company/companySelectors';
import {SettingsFieldLine} from '../common/settingsFieldLine';
import {
  SettingsFooter,
  StyledSettingsFormContent,
  StyledSettingsFormMain,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {useSettingsInboxesBanners} from './settingsInboxesCommon';
import {SettingsInboxesInactivityTimeoutField} from './settingsInboxesInactivityTimeoutField';
import {SettingsInboxesInboxHeader} from './settingsInboxesInboxHeader';
import {SettingsInboxesMassArchive} from './settingsInboxesMassArchive';

/*
 * Props.
 */

interface SettingsInboxesPreferencesProps {
  inbox: InboxModel;
  url: SettingsInboxesUrl;
  onInboxUpdate: (inbox: InboxModel) => Promise<void>;
}

/*
 * Style.
 */

const StyledHighlightDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  grid-column-gap: 5px;
  align-items: center;
  justify-content: center;
`;

const StyledLink = styled.a`
  color: ${(p) => p.theme.palette.blue.shade40};
  display: inline-block;
  &:hover {
    color: ${(p) => p.theme.palette.blue.shade50};
  }
`;

/*
 * Component.
 */

export const SettingsInboxesPreferences: FC<SettingsInboxesPreferencesProps> = (props) => {
  const {url, inbox, onInboxUpdate} = props;
  const theme = useUserTheme();
  const canUseCompanyRules = useAppSelector(getCompanyHasGlobalRules);
  const [inboxColor, setInboxColor] = useState(inbox.color);
  const [editedInbox, setEditedInbox] = useState(inbox);
  const [isDirty, setIsDirty] = useState(false);

  const onColorSelect: HandlerOf<PaletteColorsEnum> = (color) => {
    const themePalette = lightTheme.palette;
    setIsDirty(true);
    setInboxColor(themePalette[color].shade40);
  };

  const inboxPaletteColor = legacyInboxColorToPaletteColor(inboxColor, theme);

  const renderColorPicker = () => (
    <StyledHighlightDiv>
      <PaletteColorPicker onColorSelect={onColorSelect} paletteColor={inboxPaletteColor} />
    </StyledHighlightDiv>
  );
  const {showUpdateBanner} = useSettingsInboxesBanners();

  const onCancelClick = () => {};
  const onOkClick = async () => {
    await onInboxUpdate({
      ...inbox,
      color: inboxColor,
      isAnalyticsVisible: editedInbox.isAnalyticsVisible,
      areGlobalRulesEnabled: editedInbox.areGlobalRulesEnabled,
      inactivityTimeout: editedInbox.inactivityTimeout,
      unassignInactive: editedInbox.unassignInactive,
      isSplit: editedInbox.isSplit,
    });
    showUpdateBanner(inbox.name);
    setIsDirty(false);
  };

  const maybeRenderFooter = () => {
    if (!isDirty) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancelClick}>
          <Trans id="settingstags-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onOkClick} isDisabled={!isDirty}>
          <Trans id="settingstags-saveinbox">Save</Trans>
        </Button>
      </SettingsFooter>
    );
  };

  return (
    <StyledSettingsWrapper>
      <SettingsInboxesInboxHeader url={url} inbox={inbox} />
      <StyledSettingsFormMain>
        <StyledSettingsFormContent>
          <SettingsFieldLine
            title={<Trans id="settingsinboxes-color-title">Color</Trans>}
            subtitle={
              <Trans id="settingsinboxes-color-subtitle">
                Will appear on every conversation that is part of this inbox.
              </Trans>
            }
            content={renderColorPicker()}
            isSubtitleMultiline
          />
          {isTeammateNamespace(inbox.namespace) && (
            <SettingsFieldLine
              title={
                <Trans id="settingsinboxes-isanalyticsvisible-title">
                  Make inbox visible in the analytics
                </Trans>
              }
              subtitle={
                <Trans id="settingsinboxes-isanalyticsvisible-subtitle">
                  Administrators will be able to run analytics about this inbox.
                </Trans>
              }
              content={
                <Toggle
                  isChecked={editedInbox.isAnalyticsVisible}
                  onChange={(v) => {
                    setIsDirty(true);
                    setEditedInbox((i) => ({...i, isAnalyticsVisible: v}));
                  }}
                />
              }
              isSubtitleMultiline
            />
          )}
          {canUseCompanyRules && (
            <SettingsFieldLine
              title={
                <Trans id="settingsinboxes-areglobarulesenabled-title">Company rules affect this inbo</Trans>
              }
              subtitle={
                <Trans id="settingsinboxes-areglobarulesenabled-subtitle">
                  Rules set by administrators can be applied before other rules on this inbox.
                </Trans>
              }
              content={
                <Toggle
                  isChecked={editedInbox.areGlobalRulesEnabled}
                  onChange={(v) => {
                    setIsDirty(true);
                    setEditedInbox((i) => ({...i, areGlobalRulesEnabled: v}));
                  }}
                />
              }
              isSubtitleMultiline
            />
          )}
          <SettingsInboxesInactivityTimeoutField
            inactivityTimeout={editedInbox.inactivityTimeout}
            onTimeoutSelect={(v) => {
              setIsDirty(true);
              setEditedInbox((i) => ({...i, inactivityTimeout: v}));
            }}
          />

          <SettingsFieldLine
            title={<Trans id="settingsinboxes-unassigninactive-title">Unassign inactive conversations</Trans>}
            subtitle={
              <Trans id="settingsinboxes-unassigninactive-subtitle">
                Inactive conversations will get unassigned when they receive a new message.
              </Trans>
            }
            content={
              <Toggle
                isChecked={editedInbox.unassignInactive}
                onChange={(v) => {
                  setIsDirty(true);
                  setEditedInbox((i) => ({...i, unassignInactive: v}));
                }}
              />
            }
            isSubtitleMultiline
          />

          {isTeammateNamespace(inbox.namespace) && (
            <SettingsFieldLine
              title={
                <Trans id="settingsinboxes-issplit-title">
                  Keep actions in this inbox separate from other inboxes
                </Trans>
              }
              subtitle={
                <>
                  <Trans id="settingsinboxes-issplit-subtitle">
                    Assign, archive, or take other actions in this inbox without affecting conversations in
                    other inboxes. Only applies to new conversations.
                  </Trans>{' '}
                  <StyledLink
                    href="https://help.frontapp.com/t/g9hz6h8/keep-actions-separate-in-shared-inboxes"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Trans id="learnmore">Learn more</Trans>
                  </StyledLink>
                </>
              }
              content={
                <Toggle
                  isChecked={editedInbox.isSplit}
                  onChange={(v) => {
                    setIsDirty(true);
                    setEditedInbox((i) => ({...i, isSplit: v}));
                  }}
                />
              }
              isSubtitleMultiline
            />
          )}

          <SettingsInboxesMassArchive inbox={inbox} />
        </StyledSettingsFormContent>
      </StyledSettingsFormMain>
      {maybeRenderFooter()}
    </StyledSettingsWrapper>
  );
};
